import React, { useMemo } from 'react';
import FleetWidget from 'models/fleet-management/FleetWidget';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { getCumulativeChartData, isCumulativeField } from 'utils/fleet-filters';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import Box from '@mui/material/Box';
import CustomLineChart from 'components/data-widgets/CustomLineChart';
import { createFleetAxisTooltipContent } from './FleetChartTooltip';

const CumulativeChartWidget: React.FC<{
  widget: FleetWidget;
  data: any[] | null;
  getStatus: HttpFetchStatus;
  width: number;
  height: number;
}> = ({ widget, data, width, height, getStatus }) => {
  const { t } = useTranslation();

  const axisData = useMemo(() => getCumulativeChartData(widget, data), [widget, data]);

  const axisContent = useMemo(
    () => createFleetAxisTooltipContent(axisData.groupMeta),
    [axisData.groupMeta],
  );

  if (
    widget.fields == null ||
    widget.fields.length < 1 ||
    widget.bucketField == null ||
    widget.bucket == null ||
    !isCumulativeField(widget.fields[0])
  ) {
    return <CustomAlert header={t('error-chart-incorrectly-configured')} severity="error" />;
  }

  return (
    <Box sx={{ height, width, overflow: 'hidden' }}>
      <CustomLineChart
        loading={getStatus === HttpFetchStatus.Loading}
        parentHeight
        tooltip={{ trigger: 'axis', slots: { axisContent } }}
        xAxis={[
          {
            data: axisData.groups,
            label: `${t('fleet-management-field-' + widget.bucketField)} (${t(
              'label-bucket-size-' + widget.bucket,
            )})`,
            scaleType: 'band',
          },
        ]}
        series={axisData.datasets.map((ds) => ({
          data: ds.data,
          label: ds.label,
        }))}
        yLabel="Values"
      />
    </Box>
  );
};

export default CumulativeChartWidget;
